/**
 * AI Prompt Inference
 *
 * Analyses the user's text prompt and infers which AI model category
 * (and therefore which default model) best fits their intent.
 *
 * Used when the user hasn't manually selected a model — so the system
 * "just works" when they type e.g. "a dog running through a field".
 */

// ── Default model for each category ──────────────────────────────────────

const CATEGORY_DEFAULTS = {
  video:           'kling-silent',
  image:           'nano-banana',
  'sound-effects': 'sound-effects',
  voiceover:       'voiceover',
};

// ── Keyword patterns mapped to categories ────────────────────────────────
// Order matters — first match wins, so more specific patterns come first.

const PROMPT_PATTERNS = [
  // Voiceover — very specific terms
  {
    category: 'voiceover',
    patterns: [
      /\bvoice\s?over\b/i,
      /\bnarrat(e|ion|or)\b/i,
      /\btts\b/i,
      /\btext[\s-]to[\s-]speech\b/i,
      /\bread\s+(this\s+)?aloud\b/i,
      /\bspeak\b/i,
      /\bspeech\b/i,
    ],
  },
  // Sound effects
  {
    category: 'sound-effects',
    patterns: [
      /\bsound\s?effect/i,
      /\bsfx\b/i,
      /\b(explosion|gunshot|footstep|thunder|rain|wind|whoosh|beep|alarm|siren)\s*(sound|noise|effect)?/i,
      /\bambien(t|ce)\b/i,
      /\bnoise\b/i,
    ],
  },
  // Image — must come before video since "generate" is ambiguous
  {
    category: 'image',
    patterns: [
      /\b(image|picture|photo|photograph|illustration|drawing|sketch|portrait|logo|icon|poster|artwork|painting)\b/i,
      /\bgenerate\s+(a\s+|an\s+)?(image|picture|photo|illustration|drawing|portrait|logo|icon)\b/i,
    ],
  },
  // Video — broadest category, acts as fallback for motion-related prompts
  {
    category: 'video',
    patterns: [
      /\bvideo\b/i,
      /\banimat(e|ion|ed)\b/i,
      /\bclip\b/i,
      /\bmovie\b/i,
      /\bscene\b/i,
      /\bfilm\b/i,
      /\b(running|walking|flying|moving|dancing|jumping|swimming|driving|falling|spinning|floating|exploding)\b/i,
      /\bcinematic\b/i,
      /\btimelapse\b/i,
      /\bslow[\s-]?mo(tion)?\b/i,
    ],
  },
];

/**
 * Infer the best model from the user's prompt text.
 * Returns the model id string, or null if no strong match is found.
 *
 * @param {string} prompt - The user's text prompt
 * @returns {string|null} Model id or null
 */
export const inferModelFromPrompt = (prompt) => {
  if (!prompt || !prompt.trim()) return null;

  for (const { category, patterns } of PROMPT_PATTERNS) {
    for (const re of patterns) {
      if (re.test(prompt)) {
        return CATEGORY_DEFAULTS[category] || null;
      }
    }
  }

  return null; // no strong match — keep current selection
};

/**
 * Infer the category from the prompt (without mapping to a specific model).
 * Useful for UI hints like "Detected: Video".
 *
 * @param {string} prompt
 * @returns {string|null} Category string or null
 */
export const inferCategoryFromPrompt = (prompt) => {
  if (!prompt || !prompt.trim()) return null;

  for (const { category, patterns } of PROMPT_PATTERNS) {
    for (const re of patterns) {
      if (re.test(prompt)) {
        return category;
      }
    }
  }

  return null;
};
